import React, { useCallback, useEffect, useRef, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { activeCompanyId, currentUserId } from '../../scripts/chat.auth';
import MessageBubble from './MessageBubble';
import { onOpenChat, OpenChatParams } from '../../scripts/chat.controller';
import { useChat } from '../../contexts/ChatContext';
import { CompanyMember } from '../../services/chat/chat.service';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { Contact } from '../../models/contact.model';
import { formatContact } from '../../scripts/format.value.script';
import { HEADER_HEIGHT } from '../../styles/layout';

interface ChatPanelProps {
  isOpen: boolean;
  isPushMode: boolean;
  onClose: () => void;
}

const PANEL_WIDTH = 400

type View = 'list' | 'thread' | 'member-select' | 'contact-select';

const ChatPanel: React.FC<ChatPanelProps> = ({isOpen, isPushMode, onClose}) => {
  const {lang} = StateLanguage()
  const companyId = activeCompanyId()

  const {
    conversations,
    activeConversationId,
    messages,
    loadingMessages,
    openConversation,
    openConversationWithUser,
    sendMessage,
    markConversationRead,
    refreshConversations,
    closeThread,
    loadMembers,
    members,
    membersUnavailable,
    getConversationName,
    getConversationSubtitle,
    getConversationAvatar,
  } = useChat()

  const [view, setView] = useState<View>('list')
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [pendingContact, setPendingContact] = useState<OpenChatParams | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Contact select state
  const [contactSearch, setContactSearch] = useState('')
  const [contactSearchDebounced, setContactSearchDebounced] = useState('')
  const [selectableContacts, setSelectableContacts] = useState<Contact[] | null>(null)
  const [contactsLoading, setContactsLoading] = useState(false)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setContactSearchDebounced(contactSearch)
    }, 300)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [contactSearch])

  // Load registered contacts when contact-select view is active
  const loadRegisteredContacts = useCallback((search: string) => {
    if (!companyId) return
    setContactsLoading(true)
    ApiDigitalAdminContact.getContacts(companyId, search, '', '', 'asc', 1, 50, undefined, true)
      .then((response) => {
        if (response.status === 200 && response.data) {
          const items = response.data.items ?? response.data ?? []
          setSelectableContacts(Array.isArray(items) ? items : [])
        } else {
          setSelectableContacts([])
        }
      })
      .catch(() => setSelectableContacts([]))
      .finally(() => setContactsLoading(false))
  }, [companyId])

  useEffect(() => {
    if (view === 'contact-select') {
      loadRegisteredContacts(contactSearchDebounced)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, contactSearchDebounced])

  // Refresh conversations when panel opens to list view
  useEffect(() => {
    if (isOpen && view === 'list') {
      refreshConversations()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, view])

  // Listen for chat:open events (from contact pages)
  useEffect(() => {
    return onOpenChat((params) => {
      if (params.contactUuid && companyId) {
        if (params.contactType === 'company') {
          setPendingContact(params)
          setView('member-select')
          loadMembers(params.contactUuid)
        } else {
          setView('thread')
          openConversationWithUser(params.contactUuid)
        }
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'})
  }, [messages])

  // When active conversation changes (via context), switch to thread view
  useEffect(() => {
    if (activeConversationId) {
      setView('thread')
    }
  }, [activeConversationId])

  // Mark read when opening a visible thread
  useEffect(() => {
    if (isOpen && activeConversationId && view === 'thread') {
      markConversationRead(activeConversationId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeConversationId, view])

  const handleSelectConversation = (conv: any) => {
    setView('thread')
    openConversation(conv)
  }

  const handleSelectMember = (member: CompanyMember) => {
    setPendingContact(null)
    setView('thread')
    openConversationWithUser(member.user_id)
  }

  const handleSelectContact = (contact: Contact) => {
    if (contact.type === 'company') {
      setPendingContact({
        contactId: contact.id,
        contactName: formatContact(contact),
        contactType: 'company',
        contactUuid: contact.data.uuid,
      })
      setView('member-select')
      loadMembers(contact.data.uuid)
    } else {
      setView('thread')
      openConversationWithUser(contact.data.uuid)
    }
    setContactSearch('')
    setSelectableContacts(null)
  }

  const handleOpenContactSelect = () => {
    setContactSearch('')
    setContactSearchDebounced('')
    setSelectableContacts(null)
    setView('contact-select')
  }

  const handleBack = () => {
    if (view === 'member-select' && pendingContact === null) {
      // Came from contact-select, go back there
      setView('contact-select')
    } else {
      closeThread()
      setPendingContact(null)
      setContactSearch('')
      setSelectableContacts(null)
      setView('list')
    }
  }

  const handleBackFromContactSelect = () => {
    setContactSearch('')
    setSelectableContacts(null)
    setView('list')
  }

  const handleBackFromMemberSelect = () => {
    setPendingContact(null)
    setView('contact-select')
  }

  const handleSend = () => {
    const text = input.trim()
    if (!text || sending) return
    setInput('')
    setSending(true)
    sendMessage(text)
    setTimeout(() => setSending(false), 100)
  }

  const formatTime = (iso: string): string => {
    const d = new Date(iso)
    return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
  }

  const getContactInitial = (contact: Contact): string => {
    if (contact.type === 'company') return (contact.data.name || '?').charAt(0).toUpperCase()
    return (contact.data.first_name || '?').charAt(0).toUpperCase()
  }

  // --- Render list ---
  const renderList = () => (
    <div className="card-body" style={{minHeight: 0, flex: '1 1 0', overflowY: 'auto'}}>
      <div className="scroll-y me-n5 pe-5">
        {conversations.length === 0 ? (
          <div className="text-center text-muted p-10">
            <div className="mb-5">
              <span className="svg-icon svg-icon-3x svg-icon-muted">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
                  <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
                  <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
                </svg>
              </span>
            </div>
            <div className="fs-7 mb-5">{lang.labels.chatNoConversations}</div>
            <button
              className="btn btn-sm btn-primary"
              type="button"
              onClick={handleOpenContactSelect}
            >
              {lang.labels.chatStartNewConversation}
            </button>
          </div>
        ) : (
          conversations.map(conv => (
            <div
              key={conv.id}
              className="d-flex align-items-center px-2 py-3 cursor-pointer bg-hover-light-primary rounded"
              role="button"
              onClick={() => handleSelectConversation(conv)}
            >
              <div className="symbol symbol-40px symbol-circle me-3">
                {getConversationAvatar(conv)
                  ? <img src={getConversationAvatar(conv)!} alt="" />
                  : <span className="symbol-label bg-light-primary text-primary fw-bold fs-6">
                      {getConversationName(conv).charAt(0).toUpperCase()}
                    </span>
                }
              </div>
              <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                <div className="d-flex justify-content-between">
                  <span className="fs-7 fw-bold text-gray-900 text-truncate">{getConversationName(conv)}</span>
                  {conv.last_message && (
                    <span className="fs-8 text-muted ms-2 flex-shrink-0">{formatTime(conv.last_message.created_at)}</span>
                  )}
                </div>
                {getConversationSubtitle(conv) && (
                  <span className="fs-8 text-primary text-truncate">{getConversationSubtitle(conv)}</span>
                )}
                {conv.last_message && (
                  <span className="fs-8 text-muted text-truncate">
                    {conv.last_message.sender_id === currentUserId() ? `${lang.labels.chatYou}: ` : ''}
                    {conv.last_message.content}
                  </span>
                )}
              </div>
              {conv.unread_count > 0 && (
                <span className="badge badge-danger badge-circle ms-2 flex-shrink-0" style={{minWidth: '20px', height: '20px', fontSize: '10px'}}>
                  {conv.unread_count}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )

  // --- Render contact select ---
  const renderContactSelect = () => (
    <div className="card-body" style={{minHeight: 0, flex: '1 1 0', overflowY: 'auto'}}>
      <div className="scroll-y me-n5 pe-5">
        <div className="px-2 py-3 mb-3">
          <input
            className="form-control form-control-sm"
            type="text"
            placeholder={lang.labels.search}
            value={contactSearch}
            onChange={e => setContactSearch(e.target.value)}
            autoFocus
          />
        </div>
        {contactsLoading ? (
          <div className="d-flex align-items-center justify-content-center p-10">
            <span className="spinner-border spinner-border-sm text-primary me-2"></span>
            <span className="text-muted fs-7">{lang.labels.loading}</span>
          </div>
        ) : selectableContacts !== null && selectableContacts.length === 0 ? (
          <div className="text-center text-muted p-10">
            <div className="fs-7 fw-bold mb-2">{lang.labels.chatNoRegisteredContacts}</div>
            <div className="fs-8">{lang.labels.chatRegisteredContactsHint}</div>
          </div>
        ) : selectableContacts !== null ? (
          selectableContacts.map(contact => (
            <div
              key={contact.id}
              className="d-flex align-items-center px-2 py-3 cursor-pointer bg-hover-light-primary rounded"
              role="button"
              onClick={() => handleSelectContact(contact)}
            >
              <div className="symbol symbol-35px symbol-circle me-3">
                <span className={`symbol-label ${contact.type === 'company' ? 'bg-light-warning text-warning' : 'bg-light-primary text-primary'} fw-bold fs-7`}>
                  {getContactInitial(contact)}
                </span>
              </div>
              <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                <span className="fs-7 fw-bold text-gray-900 text-truncate">{formatContact(contact)}</span>
                <span className="fs-8 text-muted">
                  {contact.type === 'company' ? lang.labels.company : lang.labels.person}
                </span>
              </div>
            </div>
          ))
        ) : null}
      </div>
    </div>
  )

  // --- Render thread ---
  const renderThread = () => {
    const activeConv = conversations.find(c => c.id === activeConversationId)
    const myId = currentUserId()

    // Build sender name lookup for group conversations
    const participantNames: Record<string, string> = {}
    if (activeConv?.is_group) {
      for (const p of activeConv.participants) {
        participantNames[p.user_id] = p.display_name || '?'
      }
    }

    return (
      <>
        <div className="card-body" style={{minHeight: 0, flex: '1 1 0', overflowY: 'auto'}}>
          <div className="scroll-y me-n5 pe-5">
            {loadingMessages ? (
              <div className="d-flex align-items-center justify-content-center py-10">
                <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                <span className="text-muted fs-7">{lang.labels.chatLoadingMessages}</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="d-flex align-items-center justify-content-center py-10">
                <span className="text-muted fs-7">{lang.labels.chatNoMessages}</span>
              </div>
            ) : (
              messages.map(msg => {
                const isMe = msg.sender_id === myId
                const senderName = (!isMe && activeConv?.is_group)
                  ? participantNames[msg.sender_id] || undefined
                  : undefined
                return (
                  <MessageBubble
                    key={msg.id}
                    content={msg.content}
                    isMe={isMe}
                    time={msg.created_at}
                    senderName={senderName}
                  />
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="card-footer pt-4">
          <textarea
            className="form-control form-control-flush mb-3"
            placeholder={lang.labels.chatPlaceholder}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            disabled={sending}
          />
          <div className="d-flex flex-stack">
            <div className="d-flex align-items-center me-2"></div>
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || sending}
            >
              {lang.labels.chatSend}
            </button>
          </div>
        </div>
      </>
    )
  }

  // --- Render member select ---
  const renderMemberSelect = () => (
    <div className="card-body" style={{minHeight: 0, flex: '1 1 0', overflowY: 'auto'}}>
      <div className="scroll-y me-n5 pe-5">
        <div className="px-2 py-3 border-bottom mb-3">
          <span className="fs-7 fw-bold text-gray-700">{lang.labels.chatSelectMember}</span>
          {pendingContact?.contactName && (
            <span className="fs-8 text-muted d-block">{pendingContact.contactName}</span>
          )}
        </div>
        {members === null ? (
          <div className="d-flex align-items-center justify-content-center p-10">
            <span className="spinner-border spinner-border-sm text-primary me-2"></span>
            <span className="text-muted fs-7">{lang.labels.chatLoadingMembers}</span>
          </div>
        ) : membersUnavailable ? (
          <div className="text-center text-muted fs-7 p-10">{lang.labels.chatMembersUnavailable}</div>
        ) : members.length === 0 ? (
          <div className="text-center text-muted fs-7 p-10">{lang.labels.chatNoMembers}</div>
        ) : (
          members.filter(m => m.user_id !== currentUserId()).map(member => (
            <div
              key={member.user_id}
              className="d-flex align-items-center px-2 py-3 cursor-pointer bg-hover-light-primary rounded"
              role="button"
              onClick={() => handleSelectMember(member)}
            >
              <div className="symbol symbol-35px symbol-circle me-3">
                {member.avatar
                  ? <img src={member.avatar} alt="" />
                  : <span className="symbol-label bg-light-primary text-primary fw-bold fs-7">
                      {member.first_name.charAt(0).toUpperCase()}
                    </span>
                }
              </div>
              <div className="d-flex flex-column">
                <span className="fs-7 fw-bold text-gray-900">{member.first_name} {member.last_name}</span>
                <span className="fs-8 text-muted">{member.email}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )

  // --- Header — template: settings.html:5029-5120 ---
  const activeConv = conversations.find(c => c.id === activeConversationId)

  const headerTitle = (): string => {
    if (view === 'thread' && activeConv) return getConversationName(activeConv)
    if (view === 'contact-select') return lang.labels.chatSelectContact
    if (view === 'member-select') return lang.labels.chatSelectMember
    return lang.labels.chatTitle
  }

  const renderHeader = () => (
    <div className="card-header pe-5">
      <div className="card-title">
        <div className="d-flex justify-content-center flex-column me-3">
          <div className="d-flex align-items-center">
            {view === 'thread' && (
              <button
                className="btn btn-sm btn-icon btn-active-light-primary me-2"
                type="button"
                onClick={handleBack}
                title={lang.labels.chatBack}
              >
                <span className="svg-icon svg-icon-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9.60001 11H21C21.6 11 22 11.4 22 12C22 12.6 21.6 13 21 13H9.60001V11Z" fill="currentColor"/>
                    <path opacity="0.3" d="M9.6 20V4L2.3 11.3C1.9 11.7 1.9 12.3 2.3 12.7L9.6 20Z" fill="currentColor"/>
                  </svg>
                </span>
              </button>
            )}
            {view === 'contact-select' && (
              <button
                className="btn btn-sm btn-icon btn-active-light-primary me-2"
                type="button"
                onClick={handleBackFromContactSelect}
                title={lang.labels.chatBack}
              >
                <span className="svg-icon svg-icon-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9.60001 11H21C21.6 11 22 11.4 22 12C22 12.6 21.6 13 21 13H9.60001V11Z" fill="currentColor"/>
                    <path opacity="0.3" d="M9.6 20V4L2.3 11.3C1.9 11.7 1.9 12.3 2.3 12.7L9.6 20Z" fill="currentColor"/>
                  </svg>
                </span>
              </button>
            )}
            {view === 'member-select' && (
              <button
                className="btn btn-sm btn-icon btn-active-light-primary me-2"
                type="button"
                onClick={handleBackFromMemberSelect}
                title={lang.labels.chatBack}
              >
                <span className="svg-icon svg-icon-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9.60001 11H21C21.6 11 22 11.4 22 12C22 12.6 21.6 13 21 13H9.60001V11Z" fill="currentColor"/>
                    <path opacity="0.3" d="M9.6 20V4L2.3 11.3C1.9 11.7 1.9 12.3 2.3 12.7L9.6 20Z" fill="currentColor"/>
                  </svg>
                </span>
              </button>
            )}
            <span className="fs-4 fw-bolder text-gray-900 lh-1">
              {headerTitle()}
            </span>
          </div>
          {view === 'thread' && activeConv && (
            <div className="mb-0 lh-1 mt-1">
              {getConversationSubtitle(activeConv) ? (
                <span className="fs-8 fw-bold text-primary">{getConversationSubtitle(activeConv)}</span>
              ) : (
                <>
                  <span className="badge badge-success badge-circle w-10px h-10px me-1"></span>
                  <span className="fs-7 fw-bold text-muted">{lang.labels.chatActive}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="card-toolbar">
        {view === 'list' && (
          <button
            className="btn btn-sm btn-icon btn-active-light-primary me-1"
            type="button"
            onClick={handleOpenContactSelect}
            title={lang.labels.chatNewConversation}
          >
            <i className="bi bi-plus-lg fs-3"></i>
          </button>
        )}
        <div className="btn btn-sm btn-icon btn-active-light-primary" onClick={onClose}>
          <span className="svg-icon svg-icon-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
              <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  )

  // --- Panel content — template: card w-100 rounded-0 ---
  const panelContent = (
    <div className="card w-100 rounded-0 h-100 d-flex flex-column">
      {renderHeader()}
      {view === 'list' && renderList()}
      {view === 'thread' && renderThread()}
      {view === 'member-select' && renderMemberSelect()}
      {view === 'contact-select' && renderContactSelect()}
    </div>
  )

  // --- Envelope: push mode ---
  if (isPushMode) {
    if (!isOpen) return null
    return (
      <div
        className="d-flex flex-column border-start bg-body"
        style={{
          width: `${PANEL_WIDTH}px`,
          flex: `0 0 ${PANEL_WIDTH}px`,
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          transition: 'width 0.3s ease, flex 0.3s ease',
        }}
      >
        {panelContent}
      </div>
    )
  }

  // --- Envelope: overlay mode ---
  return (
    <>
      <div id="drawer-chat" className="bg-body drawer drawer-end" style={{width: '400px'}} data-kt-drawer="true">
        {panelContent}
      </div>
      <div id="overlay-chat" onClick={onClose}></div>
    </>
  )
}

export default ChatPanel;
